import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Divider, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper } from '@mui/material';
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { AlternateEmailOutlined, AnalyticsOutlined, AssignmentOutlined, BusinessOutlined, LinkOutlined, DashboardOutlined, Edit, LocationOnOutlined, PersonOutline, ReceiptOutlined, StorefrontOutlined, EmailOutlined, NotificationsOutlined, ChatBubbleOutline, PhoneOutlined, Check, AccessTimeFilledOutlined, AdminPanelSettingsOutlined, Key, CheckOutlined } from "@mui/icons-material";
import { ContainedButton, TooltipIconButton } from "../../common/StyledButtons";
import Colors from "../../../constants/Colors";
import StyledSubHeader from "../../common/StyledSubHeader";
import { getSiteByIdForUser, getSiteSubscription, getUserByLoginIdForAdmin } from "../../../actions/internal/SiteActions";
import { getSiteById, getSiteSubscriptionById } from "../../../selectors/SiteSelectors";
import StyledProgressCircle from "../../common/StyledProgressCircle";
import { getUserId } from "../../../selectors/UserSelectors";
import { CARD_HEADER_STYLE, ICON_TEXT_SECONDARY_FONT, SUMMARY_TITLE_FONT } from "../../../constants/Styles";
import StyledChip from "../../common/StyledChip";
import IconTextSection from "../../common/IconTextSection";
import { capitalize } from "lodash";
import { getUserByLoginId } from "../../../selectors/UsersSelectors";
import AdminEditUserAccessModal from "../modals/AdminEditUserAccessModal";
import StyledPage from "../../common/StyledPage";
import { getAllTeamMembers } from "../../../selectors/AdminSelectors";
import { getAdminAccessLevel } from "../../../actions/profile/UserActions";
import { getAllTeamMembersForAdmin } from "../../../actions/internal/AdminActions";

const AdminManageUserPage = ({ history, match, location }) => {

    const userLoginId = useRef();

    const [ isLoading, setIsLoading ] = useState(true);
    const [ showEditAccessModal, setShowEditAccessModal ] = useState(false)
  
    const user = useMemo(() => {
        return getUserByLoginId(userLoginId.current);
    }, [userLoginId.current]);
      
    const isSuperUser = getAdminAccessLevel() === 100;
    const teamMembers = getAllTeamMembers();
      
    const teamMember = useMemo(() => {
        if (!user) return undefined;
        if (!teamMembers || teamMembers.length === 0) return undefined;
        
        return teamMembers.find(t => t.user_id === user.user_id);
    }, [teamMembers, user]);

    useEffect(() => {
        loadData().then();
    }, [])
  
    const loadData = async () => {
        try {
            const userId = getUserId()
            const params = new URLSearchParams(location.search);
            const loginId = params.get("u");
            if (!loginId) {
                history.replace('/admin/users');
                return;
            }
            userLoginId.current = loginId;
            await getUserByLoginIdForAdmin(loginId, userId);
            if (isSuperUser) await getAllTeamMembersForAdmin(userId);
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    }

    const renderJson = (obj) => {
        return (
          <div>
            {Object.keys(obj).map((key) => {
                const valType = typeof obj[key];
                switch (valType) {
                    case 'boolean':
                        return (<div><b>{key}</b>: {String(obj[key])}</div>)
                    default:
                        return (<div><b>{key}</b>: "{obj[key]}"</div>)
                }
            })}
          </div>
        )
      }

      const convertJsonToHtml = (obj) => {
        return (
          <div>
            {Object.keys(obj).map((key) => (<div><b>{key}</b>: "{obj[key]}"</div>))}
          </div>
        )
      }

    const renderLoadingContent = () => (
        <StyledProgressCircle />
    )

    const renderUserSecuritySection = () => (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', position: 'relative', paddingTop: '20px', paddingBottom: '20px' }}>
                <h4 style={{ margin: 0, fontSize: '20px' }}>User Security</h4>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <IconTextSection icon={<AdminPanelSettingsOutlined style={{ color: Colors.primaryColor }}/>}
                        primaryText={'Is Admin'}
                        secondaryText={user.user_security.is_admin ? 'Yes' : 'No'}
                        primaryTextStyle={SUMMARY_TITLE_FONT}
                        secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                    />
                </Grid>
            </Grid>
        </>
    )

    const renderUserDetails = () => (
        <Grid container spacing={2} sx={{ marginTop: '24px' }}>
            <Grid item xs={12}>
                <h5 style={{ padding: 0, margin: 0}}>User Details</h5>
            </Grid>
            <Grid item xs={12}>
                <Paper variant="outlined" sx={{ padding: '16px', position: 'relative' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', position: 'relative', paddingBottom: '20px' }}>
                        <h4 style={{ margin: 0, fontSize: '24px' }}>{user.user_personal.first_name} {user.user_personal.last_name}</h4>
                        <TooltipIconButton
                            text="Edit Business Account Details"
                            icon={<Edit sx={{ fontSize: '24px' }}/>}
                            onClick={() => history.push(`/admin/users/edit?u=${user.user_login_id}`)}
                        />
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <IconTextSection
                                icon={<PersonOutline style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Username'}
                                secondaryText={user.user_login_id}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <IconTextSection
                                icon={<EmailOutlined style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Email'}
                                secondaryText={user.user_contact.email.primary || <i>Not Provided</i>}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <IconTextSection
                                icon={<PhoneOutlined style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Phone'}
                                secondaryText={user.user_contact.phone.primary || <i>Not Provided</i>}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', position: 'relative', paddingTop: '20px', paddingBottom: '20px' }}>
                        <h4 style={{ margin: 0, fontSize: '20px' }}>Contact Preferences</h4>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <IconTextSection icon={<NotificationsOutlined style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Promotions Notification'}
                                secondaryText={renderPromotionalNotificationText(user.user_contact)}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <IconTextSection icon={<NotificationsOutlined style={{ color: Colors.primaryColor }}/>}
                                primaryText={'General Notification'}
                                secondaryText={renderGeneralNotificationText(user.user_contact)}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <IconTextSection icon={<ChatBubbleOutline style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Primary Contact Method'}
                                secondaryText={renderPrimaryContactMethodText(user.user_contact)}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                    </Grid>
                    {isSuperUser &&
                       renderUserSecuritySection()
                    }
                    {isSuperUser &&
                        <>
                            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', position: 'relative', paddingTop: '20px', paddingBottom: '20px' }}>
                                <h4 style={{ margin: 0, fontSize: '20px' }}>External Connections</h4>
                            </Box>
                            { !user || !user.user_external_connections || Object.keys(user.user_external_connections).length === 0 ? <em>No connections applied</em> :
                                Object.keys(user.user_external_connections).map((key) => (
                                <IconTextSection icon={<Check />} primaryText={capitalize(key)} secondaryText={convertJsonToHtml(user.user_external_connections[key])} />
                                ))
                            }
                            </>
                    }
                    
                </Paper>
            </Grid>
        </Grid>
    )

    const renderTeamMemberDetails = () => (
        <Grid container spacing={2} sx={{ marginTop: '24px' }}>
            <Grid item xs={12}>
                <h5 style={{ padding: 0, margin: 0}}>Teammate Details</h5>
            </Grid>
            <Grid item xs={12}>
                <Paper variant="outlined" sx={{ padding: '16px', position: 'relative' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', position: 'relative', paddingBottom: '20px' }}>
                        <h4 style={{ margin: 0, fontSize: '24px' }}>{teamMember.team_member_type}</h4>
                        {/* <TooltipIconButton
                            text="Edit Teammate Details"
                            icon={<Edit sx={{ fontSize: '24px' }}/>}
                            onClick={() => history.push(`/admin/users/edit?u=${user.user_login_id}`)}
                        /> */}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <IconTextSection
                                icon={<PersonOutline style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Role'}
                                secondaryText={teamMember.team_role}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <IconTextSection
                                icon={<AccessTimeFilledOutlined style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Clockify'}
                                secondaryText={teamMember.clockify ? 'Connected' : 'Not Connected'}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <IconTextSection
                                icon={<CheckOutlined style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Status'}
                                secondaryText={teamMember.is_active ? 'Active' : 'Inactive'}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <IconTextSection
                                icon={<Key style={{ color: Colors.primaryColor }}/>}
                                primaryText={'Access Level'}
                                secondaryText={teamMember.access_level || 'Not Assigned'}
                                primaryTextStyle={SUMMARY_TITLE_FONT}
                                secondaryTextStyle={ICON_TEXT_SECONDARY_FONT}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    )

    const renderPromotionalNotificationText = (user_contact) => {
        if (!user_contact.preferences.promotional_notification 
            || user_contact.preferences.promotional_notification === null
            || user_contact.preferences.promotional_notification === "none") {
            return "Disabled";
        }
        return "Enabled"
    }

    const renderGeneralNotificationText = (user_contact) => {
        if (!user_contact.preferences.general_notification 
            || user_contact.preferences.general_notification === null
            || user_contact.preferences.general_notification === "none") {
            return "Disabled";
        }
        return "Enabled"
    }

    const renderPrimaryContactMethodText = (user_contact) => {
        if (!user_contact.preferences.system_notification 
            || user_contact.preferences.system_notification === null
            || user_contact.preferences.system_notification === "none") {
            return "Unknown";
        }

        const path = user_contact.preferences.system_notification.split(".");
        return capitalize(path[0]);
    }

    const renderUserAccess = () => (
        <Grid container spacing={2} sx={{ marginTop: '24px' }}>
            <Grid item xs={12}>
                <h5 style={{ padding: 0, margin: 0}}>Business Access</h5>
            </Grid>
            <Grid item xs={12}>
                <Paper variant="outlined" style={{ padding: '24px 16px 24px 16px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Accounts</h4>
                    {/*<TooltipIconButton
                        text="Manage User Access"
                        icon={<Edit sx={{ fontSize: '24px' }}/>}
                        onClick={() => setShowEditAccessModal(true)}
                    />*/}
                    </Box>
                    
                    <List style={{ flex: 1 }}>
                    {user && user.user_sites.map((site) => (
                        <ListItem
                            dense
                            button
                            onClick={() => history.push(`/admin/sites/${site.site_id}/manage`)}
                            sx={{ backgroundColor: '#fff', marginBottom: '5px' }}>
                            <ListItemAvatar>
                                {site.site_details.site_img_url ?
                                    <Avatar alt={site.site_details.site_name} src={site.site_details.site_img_url} sx={{ boxShadow: '0px 0px 11px 0px rgba(0,0,0,0.31)'}} />
                                    :
                                    <Avatar sx={{ boxShadow: '0px 0px 11px 0px rgba(0,0,0,0.31)'}}><StorefrontOutlined /></Avatar>
                                }
                            </ListItemAvatar>
                            <ListItemText primary={`${site.site_details.site_name}`} />
                                {site.is_primary_user && (<ListItemSecondaryAction>
                                <StyledChip text="Primary" />
                            </ListItemSecondaryAction>
                            )}
                        </ListItem>
                    ))}
                    </List>
                    {user.user_sites.length === 0 &&
                        <p><em>No access to any accounts.</em></p>
                    }
                </Paper>
            </Grid>
        </Grid>
    )

    const renderDescription = (description) => description.split('\n').reduce((acc, val)=> [...acc, <div>{val}</div>],[])

    const renderUserAdditionalInformation = () => (
        <Grid container spacing={2} sx={{ marginTop: '24px' }}>
            <Grid item xs={12}>
                <h5 style={{ padding: 0, margin: 0}}>Additional Information</h5>
            </Grid>
            <Grid item xs={12}>
                <Paper variant="outlined" style={{ padding: '24px 16px 24px 16px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Notes</h4>
                    <TooltipIconButton
                            text="Edit Business Account Details"
                            icon={<Edit sx={{ fontSize: '24px' }}/>}
                            onClick={() => history.push(`/admin/users/edit?u=${user.user_login_id}`)}
                        />
                    </Box>
                    
                    <Box>{user.user_notes ? renderDescription(user.user_notes.notes_text) : <em>No notes provided</em>}</Box>
                </Paper>
            </Grid>
        </Grid>
    )

    // Implement Search and Pagination next
    return (
        <StyledPage>
            <StyledSubHeader title="Manage User Account" />
            {isLoading ? renderLoadingContent() :
                <div>
                    {renderUserDetails()}
                    {renderUserAccess()}
                    {isSuperUser&& teamMember !== undefined && renderTeamMemberDetails()}
                    {renderUserAdditionalInformation()}
                </div>
            }
            <AdminEditUserAccessModal
                open={showEditAccessModal}
                onClose={() => setShowEditAccessModal(false)}
                site={null}
            />
        </StyledPage>
    )
}

export default withRouter(observer(AdminManageUserPage));
